import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import PageTransition from '../components/PageTransition';
import { SITE_CONFIG, CONTACT_PAGE } from '../data';
import './ContactPage.css';

export default function ContactPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const d = CONTACT_PAGE;

  const contactItems = [
    { icon: <Mail size={20} />, label: d.labels.email,       value: SITE_CONFIG.email,    href: `mailto:${SITE_CONFIG.email}` },
    { icon: <Phone size={20} />, label: d.labels.phone,      value: SITE_CONFIG.phone,    href: `tel:${SITE_CONFIG.phone}` },
    { icon: <MapPin size={20} />, label: d.labels.address,   value: SITE_CONFIG.location, href: null },
    { icon: <Clock size={20} />,  label: d.labels.responseTime, value: d.labels.responseVal, href: null },
  ];

  return (
    <PageTransition>
      <div className="contact-page">

        <section className="page-hero">
          <div className="page-hero-bg" />
          <div className="container page-hero-content">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="section-tag">{d.hero.tag}</span>
              <h1>{d.hero.title}</h1>
              <p>{d.hero.description}</p>
            </motion.div>
          </div>
        </section>

        <section className="contact-main">
          <div className="container contact-layout">

            {/* ── Contact Info ── */}
            <motion.div
              className="contact-info-block"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2>{d.sectionTitle}</h2>
              <p className="contact-intro">{d.sectionDescription}</p>

              <div className="contact-cards">
                {contactItems.map((item, i) => (
                  <motion.div
                    key={item.label}
                    className="contact-card"
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08 }}
                  >
                    <div className="contact-card-icon">{item.icon}</div>
                    <div className="contact-card-body">
                      <span className="contact-card-label">{item.label}</span>
                      {item.href
                        ? <a href={item.href}>{item.value}</a>
                        : <span>{item.value}</span>
                      }
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Social media strip */}
              <div className="contact-social">
                <a href={SITE_CONFIG.instagram} target="_blank" rel="noopener noreferrer" className="contact-social-btn">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><rect x="2" y="2" width="20" height="20" rx="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
                  Instagram
                </a>
                <a href={SITE_CONFIG.twitter} target="_blank" rel="noopener noreferrer" className="contact-social-btn">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                  Twitter / X
                </a>
                <a href={SITE_CONFIG.linkedin} target="_blank" rel="noopener noreferrer" className="contact-social-btn">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2z"/><circle cx="4" cy="4" r="2"/></svg>
                  LinkedIn
                </a>
              </div>
            </motion.div>

            {/* ── FAQ ── */}
            <motion.div
              className="faq-block"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
            >
              <h2>{d.faqTitle}</h2>
              <div className="faq-list">
                {d.faq.map((item, i) => (
                  <div
                    key={i}
                    className={`faq-item ${openFaq === i ? 'open' : ''}`}
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <div className="faq-question">
                      <span>{item.q}</span>
                      <svg
                        viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"
                        style={{ transform: openFaq === i ? 'rotate(180deg)' : 'none', transition: 'transform 0.25s', flexShrink: 0 }}
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </div>
                    {openFaq === i && (
                      <motion.div
                        className="faq-answer"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.22 }}
                      >
                        <p>{item.a}</p>
                      </motion.div>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>

          </div>
        </section>

      </div>
    </PageTransition>
  );
}
